import { useCallback, useEffect, useRef } from 'react';
import { isbot } from 'isbot';

export interface UseAssembledChatScriptOptions {
  user_signed_in: boolean;
  ASSEMBLED_ENABLED: boolean;
  assembled_jwt_token?: string; // Generated JWT token from backend with user info
  mobile_in_app_chat_enabled?: boolean;
}

/**
 * Hook to conditionally load the Assembled HQ chat script on the client-side.
 * Appends the script to document.head.
 *
 * Also supports opening the chat automatically via URL parameter:
 * - Add ?openChat=true to the URL to automatically open the chat
 *
 * For logged-in users, authenticates with JWT token using Assembled's API.
 */
export function useAssembledChatScript({
  user_signed_in,
  ASSEMBLED_ENABLED,
  assembled_jwt_token,
  mobile_in_app_chat_enabled = false,
}: UseAssembledChatScriptOptions): void {
  const shouldLoad = ASSEMBLED_ENABLED;
  const shouldShowForLoggedInUser = user_signed_in && mobile_in_app_chat_enabled;
  const wasPreviouslySignedIn = useRef(false);

  const shouldSkipExecution = useCallback(() => {
    return (
      !shouldLoad ||
      (navigator.userAgent && isbot(navigator.userAgent)) ||
      (user_signed_in && !mobile_in_app_chat_enabled)
    );
  }, [shouldLoad, user_signed_in, mobile_in_app_chat_enabled]);

  const createRetryLogic = (
    operation: () => Promise<void> | void,
    maxRetries: number = 10,
    retryDelay: number = 100,
    errorMessage: string
  ) => {
    let retryCount = 0;
    let timeoutId: NodeJS.Timeout | null = null;
    const executeWithRetry = async () => {
      try {
        await operation();
      } catch (error) {
        console.error(errorMessage, error);
        if (retryCount < maxRetries) {
          retryCount++;
          timeoutId = setTimeout(executeWithRetry, retryDelay);
        } else {
          console.warn(`Failed after maximum retries: ${errorMessage}`);
        }
      }
    };
    const start = () => {
      timeoutId = setTimeout(executeWithRetry, 500);
    };
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    return { start, cleanup };
  };

  // Script loading effect
  useEffect(() => {
    if (shouldSkipExecution()) return;

    const scriptId = 'assembled-base-script';
    const existingScript = document.getElementById(scriptId);
    if (existingScript) return;

    const attributes = {
      'data-company-id': 'df07c1d6-20b4-403d-8959-376d258ed86d',
      'data-profile-id': shouldShowForLoggedInUser
        ? '58c58964-2a4c-4915-ac54-34485516b349'
        : 'b9b2c35f-d083-4b94-8a4b-cdddd097f9c2',
    };

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://cal.assembledhq.com/static/js/public-chat.js';
    script.async = true;
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value as string);
    });
    document.head.appendChild(script);

    const observer = new MutationObserver(() => {
      const launcher = document.querySelector<HTMLElement>('assembled-chat-launcher');
      const iframe = document.querySelector<HTMLIFrameElement>('iframe.assembled-chat');

      if (launcher) {
        launcher.style.zIndex = '40';
        launcher.setAttribute('launcher-z-index', '40');
      }

      if (iframe) {
        iframe.style.zIndex = '40';
      }

      if (launcher && iframe) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [shouldLoad, shouldSkipExecution, shouldShowForLoggedInUser]);

  // Authentication effect
  useEffect(() => {
    if (
      shouldSkipExecution() ||
      !shouldLoad ||
      !assembled_jwt_token ||
      !shouldShowForLoggedInUser
    ) {
      return;
    }
    const { start, cleanup } = createRetryLogic(
      async () => {
        if (window.assembled?.authenticateUser) {
          await window.assembled.authenticateUser(assembled_jwt_token);
        } else {
          throw new Error('authenticateUser method not available');
        }
      },
      10,
      100,
      'Error authenticating user with Assembled'
    );
    start();
    return cleanup;
  }, [shouldLoad, assembled_jwt_token, shouldShowForLoggedInUser, shouldSkipExecution]);

  // Logout effect
  useEffect(() => {
    if (shouldSkipExecution() || !shouldLoad) {
      return;
    }
    if (!user_signed_in && wasPreviouslySignedIn.current && window.assembled) {
      try {
        if (window.assembled.close) {
          window.assembled.close();
        }
        if (window.assembled.authenticateUser) {
          window.assembled.authenticateUser('').catch(() => {});
        }
      } catch (error) {
        console.error('Error clearing Assembled authentication:', error);
      }
    }

    wasPreviouslySignedIn.current = user_signed_in;
  }, [shouldLoad, user_signed_in, shouldSkipExecution]);

  // URL parameter chat opening effect
  useEffect(() => {
    if (shouldSkipExecution()) {
      return;
    }
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const shouldOpenChat = urlParams.get('openChat') === 'true';
      if (!shouldOpenChat) {
        return;
      }
      const { start, cleanup } = createRetryLogic(
        () => {
          if (window.assembled?.isReady?.() && window.assembled?.open) {
            window.assembled.open();
          } else {
            throw new Error('Assembled not ready or open method not available');
          }
        },
        10,
        100,
        'Error in checkAndOpenChat'
      );
      start();
      return cleanup;
    } catch (error) {
      console.error('Error handling URL parameter for chat trigger:', error);
    }
  }, [shouldLoad, shouldSkipExecution]);
}
